import { autoinject, bindable, computedFrom } from "aurelia-framework";
import { transient } from "aurelia-dependency-injection";
import { LookupClientService } from "../../../../services/lookups/lookup-client.service";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { LookupValueResponse, LookupValueRequest, LookupValue } from "../../../../models/lookups/lookup.models";
import { LookupUtilities } from "../../../../utilities/lookup-utilities";
import { DialogServiceConstants } from "../../../../constants/basics/dialog.constants";
import { LookupAdditionalData } from "../../../../constants/lookups/lookup.constants";
import { Reflection } from "../../../../utilities/reflection";
import { LookupControl } from "../lookup-control/lookup-control";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";

@autoinject
@transient()
export class LookupHierarchyControl {

    private _code: string;
    private _value: string;
    private _itemSource: any[];

    public selectedItemInternal: any;

    @bindable
    public isClearEnabled: string = null;

    @bindable
    public cacheFlagKey: string = "";

    @bindable
    public query: string;

    @bindable
    public label: string = "";

    @bindable
    public readonly: boolean = false;

    @bindable
    public disabled: boolean = false;

    @bindable
    public info: string = "";

    @bindable
    public parameters: string = "";

    @bindable
    public excludeItemsById: string | number | any[] = [];

    @bindable
    public selectedItem: any;

    @bindable
    public selectedValue: number;

    @bindable
    public selectedValuePath: string = "id";

    @bindable
    public addAllOption: boolean = false;

    @bindable
    public isBrowseIcon: boolean = false;

    @computedFrom(Reflection.getPropertyPath<LookupHierarchyControl>(z => z._itemSource))
    public get itemSource(): any[] {
        return this._itemSource;
    }

    public set itemSource(x: any[]) {
        this._itemSource = x;
        this.rebind();
    }

    @computedFrom(Reflection.getPropertyPath<LookupHierarchyControl>(x => x.itemSource),
        Reflection.getPropertyPath<LookupHierarchyControl>(x => x.selectedValue),
        Reflection.getPropertyPath<LookupHierarchyControl>(x => x.excludeItemsById))
    public get itemSourceFiltered() {
        const excludedIds = LookupControl.filterIds(this.excludeItemsById);

        let filtered;

        if (this.itemSource) {
            filtered = CloneHelper.cloneObject(this.itemSource);

            if (this.itemSource.length > 0 && excludedIds.length > 0) {
                filtered = this.filterItemSourceRecursively(filtered, excludedIds);
            }
        } else {
            filtered = null;
        }

        return (!filtered || filtered.length == 0) ? null : filtered;
    }

    @computedFrom(Reflection.getPropertyPath<LookupHierarchyControl>(z => z._code))
    public get code(): string {
        return this._code;
    }

    public set code(value: string) {
        if (this._code !== value) {
            this._code = value;
            this.selectedItem = this.searchByCode(this.code, this.itemSource, 0);
            this.selectedValue = this.getValue(this.selectedItem);
        }
    }

    @computedFrom(Reflection.getPropertyPath<LookupHierarchyControl>(z => z.selectedItem))
    public get value(): string {
        return this.selectedItem ? this.selectedItem.name : this._value;
    }

    public set value(value: string) {
        if (this._value !== value) {
            this._value = value;
            this.selectedItem = this.searchByName(this._value, this.itemSource, 0);
            this.selectedValue = this.getValue(this.selectedItem);
            if (!this.selectedItem) {
                this._code = null;
            }
        }
    }

    constructor(private element: Element, protected lookupClient: LookupClientService, protected dialogClient: DialogClientService) {
    }

    public queryChanged() {
        this.loadData();
    }

    public parametersChanged() {
        this.loadData();
    }

    public selectedItemChanged(newValue: any): void {
        this._code = newValue ? (newValue.additionalData[LookupAdditionalData.code] || '').trim() : this._code;
        if (this.itemSource) {
            setTimeout(() => {
                const itemChangedEvent = new CustomEvent("selected-item-changed", {
                    detail: { item: newValue },
                    bubbles: true
                });
                this.element.dispatchEvent(itemChangedEvent);
            });
        }
    }

    bind() {
        this.loadData();
    }

    public getValue(item: any): any {
        return this.selectedValuePath && item ? item[this.selectedValuePath] : item;
    }

    public openModal() {
        this.dialogClient.show(DialogServiceConstants.URLs.LookupHierarchyControlModal, this);
    }

    public selectItem(lookup: any) {
        if (<any>lookup.additionalData[LookupAdditionalData.isSelectable]) {
            this.selectedItemInternal = lookup;
        }
    }

    public selectedValueChanged(newValue: number, oldValue: number) {
        if (newValue === oldValue) return;
        this.rebind();
        setTimeout(() => {
            const valueChangedEvent = new CustomEvent("selected-value-changed", {
                detail: { value: newValue },
                bubbles: true
            });
            this.element.dispatchEvent(valueChangedEvent);
        });
    }

    private rebind() {
        if (this.selectedValue && this.itemSource) {
            this.selectedItem = LookupUtilities.matchLookupByIdRecursively(<LookupValue[]>this.itemSource, Number(this.selectedValue));
            this.selectedItemInternal = this.selectedItem;
        }
    }

    private filterItemSourceRecursively(itemSource, excludedIds: any[]) {
        itemSource = itemSource.filter(x => !excludedIds.some(y => y == x.id) || x.id == this.selectedValue);

        itemSource.forEach(x => {
            if (x.children && x.children.length > 0) {
                x.children = this.filterItemSourceRecursively(x.children, excludedIds);
            }
        });

        return itemSource;
    }

    protected loadData() {
        const lookupValueRequest = this.createLookupValueRequest();
        this.lookupClient.getLookup(lookupValueRequest, this.cacheFlagKey)
            .then((response: LookupValueResponse) => {
                this.itemSource = response.allValuesIncludingInactive;
                this.rebind();
                if (this.addAllOption && this.itemSource.filter(x => x.name == "All").length == 0) {
                    this.itemSource.push({ name: "All", id: 0, children: [], additionalData: { isSelectable: true, code: 'ALL' } })
                }
            }).catch(() => {
            });
    }

    protected createLookupValueRequest(): LookupValueRequest {
        const request = new LookupValueRequest();
        request.lookupValueQuery = this.query;
        request.parameters = this.parameters;
        return request;
    }

    private searchByCode(code: string, lookupValues: LookupValue[], level: number): LookupValue {
        if (lookupValues) {
            for (const lookupValue of lookupValues) {
                const additionalData = lookupValue.additionalData[LookupAdditionalData.code];
                const value: LookupValue = (additionalData ? additionalData.trim().toLowerCase() : '') ===
                    code.trim().toLowerCase() &&
                    (<any>lookupValue.additionalData[LookupAdditionalData.isSelectable]) ? lookupValue : null;

                if (value) {
                    return value;
                }
                else if (lookupValue.children && lookupValue.children.length > 0) {
                    const childrenResult = this.searchByCode(code, lookupValue.children, level + 1);
                    if (childrenResult !== null) {
                        return childrenResult;
                    }
                }
            }
        }
        return null;
    }

    private searchByName(searchValue: string, lookupValues: LookupValue[], level: number): LookupValue {
        searchValue = searchValue ? searchValue.trim().toLowerCase() : "";
        if (lookupValues) {
            for (const lookupValue of lookupValues) {
                const lookupName = lookupValue.name ? lookupValue.name.trim().toLowerCase() : "";
                const value: LookupValue = lookupName === searchValue && lookupValue.additionalData[LookupAdditionalData.isSelectable] ? lookupValue : null;

                if (value) {
                    return value;
                }
                else if (lookupValue.children && lookupValue.children.length > 0) {
                    const childrenResult = this.searchByName(searchValue, lookupValue.children, level + 1);
                    if (childrenResult !== null) {
                        return childrenResult;
                    }
                }
            }
        }
        return null;
    }
}
